import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AboutSection = styled.section`
  padding: 5rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImage = styled(motion.div)`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 3/4;
  max-width: 500px;
  background: url('/assets/Marcella.jpg') center/cover;
  box-shadow: ${props => props.theme.shadows.card};
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(46, 204, 113, 0.1), rgba(34, 197, 94, 0.1));
  }
`;

const AboutContent = styled(motion.div)`
  h2 {
    font-family: ${props => props.theme.fonts.script};
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
`;

const HighlightBox = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 2rem 0;
  box-shadow: ${props => props.theme.shadows.sm};
  border-left: 4px solid ${props => props.theme.colors.primary};
  
  p {
    margin-bottom: 0;
    font-style: italic;
    color: ${props => props.theme.colors.text};
  }
`;

const AboutButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
  }
`;

const AboutPreview = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <AboutSection ref={ref}>
      <Container>
        <AboutGrid>
          <AboutImage
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          />
          
          <AboutContent
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2>Marcella Deeken</h2>
            <h3>Ihre Expertin für tiergestützte Pädagogik</h3>
            
            <p>
              Seit vielen Jahren lebe ich meine Leidenschaft für die Arbeit mit Ponys und Menschen. 
              Mit Herz und Verstand schaffe ich einen sicheren Raum, in dem Menschen jeden Alters 
              durch die Begegnung mit unseren Ponys wachsen, ihre Stärken entdecken und Vertrauen 
              zu sich selbst und anderen aufbauen können.
            </p>
            
            <HighlightBox>
              <p>
                "Ponys sind wunderbare Lehrmeister. Sie spiegeln unsere Emotionen, 
                lehren uns Geduld und schenken uns bedingungslose Akzeptanz."
              </p>
            </HighlightBox>
            
            <p>
              Die Begegnung zwischen Mensch und Tier ist etwas ganz Besonderes. 
              In meiner Arbeit erlebe ich täglich, wie bereichernd und heilsam diese 
              Verbindung sein kann.
            </p>
            
            <AboutButton to="/marcella">
              Mehr über mich erfahren
            </AboutButton>
          </AboutContent>
        </AboutGrid>
      </Container>
    </AboutSection>
  );
};

export default AboutPreview;